import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import bindMethods from 'auto-bind';
import IdleTimer from 'react-idle-timer';
import loadScripts from 'load-script';
import GoogleAnalytics from 'react-ga';

import NavigationBar from '../components/NavigationBar';
import Sidebar from '../components/Sidebar';
import QlikContentArea from '../components/QlikContentArea';
import isFootball from '../state/selectors/isFootball';
import ApplicationSpinner from './ApplicationSpinner';
import { initializeQlik } from '../utils/qlik';
import { toggleVideoSettings } from '../state/settings';
import {
  clearAuthInLocalStorage,
  getVideoSettingsFromLocalStorage,
} from '../utils/localStorage';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const ContentArea = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
`;

const isProduction = process.env.NODE_ENV === 'production';
const INACTIVITY_TIMEOUT_MS = 1000 * 60 * 25;

class ApplicationLayout extends PureComponent {
  constructor(props) {
    super(props);
    bindMethods(this);

    this.state = {
      _isMounted: false,
      isLoaded: false,
    };

    this.idleTimer = React.createRef();
  }

  componentDidMount() {
    const { _isMounted } = this.state;
    const { toggleVideoSettings } = this.props;
    const doVideoSettingsExist = getVideoSettingsFromLocalStorage();

    if (doVideoSettingsExist) toggleVideoSettings(doVideoSettingsExist.thunderDefault);

    if (!_isMounted) {
      this.setState({ _isMounted: true }, async () => {
        await this.loadScripts();
        await initializeQlik();
        this.setState({ isLoaded: true });
      });
    }
  }

  onIdle() {
    const { history } = this.props;
    clearAuthInLocalStorage();
    history.push('/login');
    window.location.reload();
  }

  loadScripts() {
    const { qlikToken } = this.props;
    const scriptUrl = `${process.env.REACT_APP_QLIK_RESOURCE_URL}/assets/external/requirejs/require.js?qlikTicket=${qlikToken}`;

    return new Promise(async (resolve, reject) => {
      // await initializeQlik();
      loadScripts(scriptUrl, (error, script) => {
        if (error) {
          reject(error);
        }
        this.loadStyles();
        resolve(script);
      });
    });
  }

  loadStyles() {
    const { qlikToken } = this.props;
    const uiStyles = `${process.env.REACT_APP_QLIK_RESOURCE_URL}/autogenerated/qlikui.css?qlikTicket=${qlikToken}`;
    const clientStyles = `${process.env.REACT_APP_QLIK_RESOURCE_URL}/assets/client/client.css?qlikTicket=${qlikToken}`;
    const styles = [uiStyles, clientStyles];

    styles.forEach((style) => {
      const link = document.createElement('link');
      link.href = style;
      link.type = 'text/css';
      link.rel = 'stylesheet';
      document.getElementsByTagName('head')[0].appendChild(link);
    });
  }

  render() {
    const { isLoaded } = this.state;
    const { qlikToken, isFootball, t } = this.props;

    if (!qlikToken) {
      return null;
    }

    return (
      <React.Fragment>
        <Helmet>
          <title>{t('NavigationBar.Dashboard')}</title>
        </Helmet>
        {isLoaded ? (
          <Container>
            {isProduction && GoogleAnalytics.pageview('/')}
            <IdleTimer
              ref={this.idleTimer}
              onIdle={this.onIdle}
              timeout={INACTIVITY_TIMEOUT_MS}
            />
            <NavigationBar />
            <ContentArea>
              {isFootball && <Sidebar />}
              <QlikContentArea />
            </ContentArea>
          </Container>
        ) : (
          <ApplicationSpinner />
        )}
      </React.Fragment>
    );
  }
}

ApplicationLayout.propTypes = {
  qlikToken: PropTypes.string, // supplied from Redux
  isFootball: PropTypes.bool.isRequired, // supplied from Redux
  history: PropTypes.object.isRequired, // supplied from React Router
  toggleVideoSettings: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired, // supplied from withTranslation
};

const TranslatedComponent = withTranslation()(ApplicationLayout);

const mapStateToProps = state => ({
  qlikToken: state.auth.qlikToken,
  isFootball: isFootball(state),
});

const mapDispatchToProps = {
  toggleVideoSettings,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TranslatedComponent);
